/**
 * Bilan chiffré comparé (ADR 0015) : tableau « Toi / Si rien ne change / Budget du gouvernement », priorités du joueur
 * (pondération facultative), « Pourquoi ? » par indicateur, services au guichet avant / après. Aucun chiffre calculé ici :
 * valeurs, comparaisons et explications viennent du moteur (`scorecard`, `explainScorecard`) ; l'UI formate et range.
 *
 * Neutralité (CLAUDE.md règle 5) : « mieux » / « moins bien » se lisent dans le sens propre de chaque indicateur (« plus bas =
 * mieux pour la dette »), jamais comme un jugement sur la politique menée. Identité MAP (ADR 0018, amende ADR 0015 §4) : la
 * pastille d'écart « toi face à si rien ne change » porte la couleur d'effet de l'INDICATEUR (bleu-vert / brique, ni vert ni
 * rouge), toujours doublée d'une flèche, d'un signe et d'un mot ; montants et colonnes restent à l'encre.
 */
import type { GapItem, ScorecardComparison, ScorecardIndicator, ScorecardIndicatorDef, Source } from "../../../engine/src/types.js";
import { weightedShares, type Scorecard, type ScorecardExplanation } from "../engine-adapter.js";
import { escapeHtml as e, hoursMinutes, int, num, signed } from "../format.js";
import type { Store } from "../state.js";
import { srcButton } from "./source-info.js";
import { TANGIBLE_DEFS } from "./tangible.js";
import { pastille, plate } from "./brand.js";

export const GROUP_LABELS: Record<string, string> = { finances: "Finances publiques", economie: "Économie", services: "Services publics" };
const STATUS_LABELS: Record<ScorecardIndicator["source_status"], string> = {
  mecanique_sourcee: "mécanique sourcée",
  mecanique_partielle: "mécanique en partie sourcée",
  mecanique_non_sourcee: "mécanique non sourcée",
};
const MARK: Record<ScorecardComparison, string> = { better: "mieux", worse: "moins bien", equal: "égal", na: "—" };

/** Valeur dans l'unité de l'indicateur (heures → « 3 h 10 »). */
export function fmtValue(i: Pick<ScorecardIndicator, "unit" | "decimals">, v: number | null): string {
  if (v === null) return "—";
  if (i.unit === "heures") return hoursMinutes(v);
  return num(v, i.decimals);
}

export function fmtDelta(i: Pick<ScorecardIndicator, "unit" | "decimals">, d: number): string {
  const dec = i.unit === "heures" ? 2 : i.decimals;
  return signed(d, dec);
}

const senseText = (i: Pick<ScorecardIndicator, "better_is">) => (i.better_is === "lower" ? "plus bas = mieux" : "plus haut = mieux");

export function markHtml(c: ScorecardComparison, against: string): string {
  if (c === "na") return "";
  return `<span class="cmpm ${c}"><span class="sr">ton résultat face à ${e(against)} : </span>${MARK[c]}</span>`;
}

function defOf(store: Store, id: string): ScorecardIndicatorDef | undefined {
  return store.content.scorecard?.indicators.find((d) => d.id === id);
}

/** Chiffre de départ de la baseline (source + identifiant du fact-checker) d'un indicateur. */
function startFigure(store: Store, d: ScorecardIndicatorDef | undefined): { source: Source | "TODO"; figureId: string; year?: number } | null {
  if (!d?.start_figure) return null;
  const [block, key] = d.start_figure.split(".");
  const rec = (store.content.baseline as unknown as Record<string, Record<string, { source: Source | "TODO"; year?: number } | undefined> | undefined>)[block ?? ""];
  const f = rec?.[key ?? ""];
  return f ? { source: f.source, figureId: `baseline/${block}/${key}`, ...(f.year !== undefined ? { year: f.year } : {}) } : null;
}

/** ⓘ d'un indicateur : définition officielle, chiffre de départ (badge de fact-check), paramètres de la mécanique et leur statut. */
export function indicatorSource(store: Store, i: ScorecardIndicator, sc: Scorecard): string {
  const d = defOf(store, i.id);
  const P = store.content.params;
  const ids = i.mechanism.unsourced;
  const fig = startFigure(store, d);
  const mech = (d?.mechanism_params ?? []).flatMap((id) => (id.endsWith("*") ? Object.keys(P).filter((k) => k.startsWith(id.slice(0, -1))) : [id]));
  const sourced = mech.filter((id) => !ids.includes(id)).map((id) => P[id]).filter((p): p is NonNullable<typeof p> => !!p);
  const seen = new Set<string>();
  const sources: Array<Source | "TODO"> = [];
  for (const s of [fig?.source ?? "TODO", ...sourced.flatMap((p) => [p.source, ...(p.sources ?? [])])]) {
    if (s === "TODO") continue;
    if (seen.has(s.url)) continue;
    seen.add(s.url);
    sources.push(s);
  }
  const unsourcedLabels = ids.map((id) => P[id]?.label ?? id);
  const notes = [
    `Définition : ${i.definition}`,
    fig ? `Départ : chiffre publié${fig.year !== undefined ? ` (${fig.year})` : ""}, voir la première source ci-dessous ; la valeur de départ du tableau est celle de fin ${sc.start_year} simulée à partir de la trajectoire officielle quand elle existe.` : "Pas de chiffre publié de départ : indice du jeu.",
    `Valeurs ${sc.year} simulées par le moteur, avec la même graine et les mêmes crises pour les trois colonnes (réponses minimales pour « si rien ne change » et le budget du gouvernement).`,
    `Mécanique : ${int(i.mechanism.sourced)} paramètre${i.mechanism.sourced > 1 ? "s" : ""} sourcé${i.mechanism.sourced > 1 ? "s" : ""} sur ${int(i.mechanism.total)}${i.mechanism.two_sources ? `, dont ${int(i.mechanism.two_sources)} à deux sources ou plus` : ""} (${STATUS_LABELS[i.source_status]}).`,
    ...(unsourcedLabels.length ? [`Non sourcés (conventions ou à documenter) : ${unsourcedLabels.slice(0, 8).join(" ; ")}${unsourcedLabels.length > 8 ? ` ; et ${int(unsourcedLabels.length - 8)} autres` : ""}. Voir l'écran Hypothèses.`] : []),
  ];
  return srcButton({ title: `${i.label} (${i.unit})`, sources: sources.length ? sources : ["TODO"], status: "simulated", notes, figureId: fig?.figureId }, { inline: true });
}

/** ⓘ des colonnes de référence. */
function columnSources(store: Store, sc: Scorecard): { trend: string; plf: string } {
  const preset = store.content.presets.find((p) => p.id === sc.reference.id);
  const P = store.content.params;
  const anchor = P["outlook_policy_measures_bn"];
  const trend = srcButton(
    {
      title: "Si rien ne change",
      sources: [anchor?.source ?? "TODO", ...(anchor?.sources ?? [])],
      status: "simulated",
      notes: [
        "Tendanciel « à politique inchangée » : aucun programme, aucun financement ; trajectoire officielle de la première année moins les mesures nouvelles du budget du gouvernement (ADR 0014).",
        "Mêmes crises que ta partie, avec la réponse minimale à chacune.",
      ],
    },
    { inline: true },
  );
  const plf = srcButton(
    {
      title: preset?.label ?? sc.reference.label,
      sources: preset?.sources.length ? preset.sources : ["TODO"],
      status: "simulated",
      notes: [preset?.description ?? "", preset?.note ?? "", "Mêmes crises que ta partie, avec la réponse minimale à chacune."].filter(Boolean),
    },
    { inline: true },
  );
  return { trend, plf };
}

/* ------------------------------------------------------------------ résumé */

function countText(c: { better: number; total: number }): string {
  return `${int(c.better)} indicateur${c.better > 1 ? "s" : ""} sur ${int(c.total)}`;
}

/**
 * Décompte neutre (aucune pondération), par groupe, et part des indicateurs à mécanique non sourcée. Le décompte face à « si rien
 * ne change » est la plaque-chiffre de l'écran (IDENTITE.md §7) ; celui face au budget du gouvernement reste en tuile.
 */
export function summaryHtml(sc: Scorecard): string {
  const s = sc.summary;
  const groups = Object.entries(s.by_group)
    .map(([g, x]) => `${e(GROUP_LABELS[g] ?? g)} ${int(x.vs_trend.better)}/${int(x.vs_trend.total)}`)
    .join(" · ");
  const groupsPlf = Object.entries(s.by_group)
    .map(([g, x]) => `${e(GROUP_LABELS[g] ?? g)} ${int(x.vs_plf.better)}/${int(x.vs_plf.total)}`)
    .join(" · ");
  const nUnsourced = s.vs_trend.total - s.sourced_vs_trend.total;
  return `${plate(`Mieux que « si rien ne change » fin ${sc.year}`, `${int(s.vs_trend.better)} sur ${int(s.vs_trend.total)}`, { note: `${countText(s.vs_trend)} · ${groups}` })}
  <dl class="scsum">
    <div><dt>Mieux que ${e(lowerFirst(sc.reference.label))}</dt><dd><b>${countText(s.vs_plf)}</b><span class="xs">${groupsPlf}</span></dd></div>
  </dl>
  <p class="hint">Un simple décompte, sans score ni pondération : chaque indicateur compte pour un, la charge d'intérêts une seule fois. ${nUnsourced ? `${int(nUnsourced)} des ${int(s.vs_trend.total)} indicateurs reposent sur une mécanique non sourcée ; sans eux : ${int(s.sourced_vs_trend.better)} sur ${int(s.sourced_vs_trend.total)} face à « si rien ne change », ${int(s.sourced_vs_plf.better)} sur ${int(s.sourced_vs_plf.total)} face au gouvernement.` : ""}</p>`;
}

const lowerFirst = (s: string) => s.charAt(0).toLowerCase() + s.slice(1);

/* ------------------------------------------------------------------ tableau comparé */

export function comparisonTable(store: Store, sc: Scorecard): string {
  const cols = columnSources(store, sc);
  const order = [...new Set(sc.indicators.map((i) => i.group ?? ""))];
  const rows = order
    .map((g) => {
      const xs = sc.indicators.filter((i) => (i.group ?? "") === g);
      const head = g ? `<tr class="grp"><th colspan="4" scope="colgroup">${e(GROUP_LABELS[g] ?? g)}</th></tr>` : "";
      return (
        head +
        xs
          .map((i) => {
            const status = i.source_status === "mecanique_sourcee" ? "" : `<span class="stat ${i.source_status === "mecanique_non_sourcee" ? "none" : "part"}">${STATUS_LABELS[i.source_status]}</span>`;
            // Pastille d'écart : toujours « toi face à si rien ne change » (nommé dans l'en-tête), effet = comparaison du moteur.
            const d = i.player !== null && i.trend !== null ? i.player - i.trend : null;
            const gap =
              i.vs_trend !== "na" && d !== null
                ? pastille(i.vs_trend === "better" ? "better" : i.vs_trend === "worse" ? "worse" : "equal", i.vs_trend === "equal" ? 0 : d, i.vs_trend === "equal" ? "égal" : fmtDelta(i, d), "face à si rien ne change")
                : "";
            return `<tr data-ind="${e(i.id)}">
      <th scope="row"><span class="il">${e(i.label)}</span>${indicatorSource(store, i, sc)}
        <span class="iu">${e(i.unit)} · ${senseText(i)}${i.counted ? "" : ` · non compté (${e(i.uncounted_reason ?? "même grandeur")})`}</span>
        ${i.start !== null ? `<span class="iu">départ ${sc.start_year} : ${fmtValue(i, i.start)}</span>` : ""}
        <span class="gap">${gap}${i.vs_plf !== "na" ? `<span class="cmpm ${i.vs_plf}"><span class="sr">ton résultat </span>face au gouv. : ${MARK[i.vs_plf]}</span>` : ""}</span>
        ${status}
        <button type="button" class="ghost why" data-scwhy="${e(i.id)}">Pourquoi ?</button></th>
      <td class="you num">${fmtValue(i, i.player)}</td>
      <td class="cmpv num">${fmtValue(i, i.trend)}</td>
      <td class="cmpv num">${fmtValue(i, i.plf)}</td></tr>`;
          })
          .join("")
      );
    })
    .join("");
  return `<div class="tblwrap"><table class="tbl sctbl bilan-table">
    <caption class="sr">Tes résultats fin ${sc.year} comparés à « si rien ne change » et au budget du gouvernement, indicateur par indicateur</caption>
    <colgroup><col><col class="c-you"><col class="c-cmp"><col class="c-cmp"></colgroup>
    <thead><tr><th scope="col">Fin ${sc.year}<span class="hint">écart : toi face à « si rien ne change »</span></th><th scope="col" class="you"><span class="tag">Toi</span></th><th scope="col">Si rien ne change${cols.trend}</th><th scope="col">Budget du gouv.${cols.plf}</th></tr></thead>
    <tbody>${rows}</tbody></table></div>
    <div class="bilan-foot"><p class="hint">Couleur de l'écart : effet sur le pays dans le sens propre de l'indicateur (plus bas = mieux pour la dette, plus haut = mieux pour la croissance), toujours avec une flèche et un signe. Ce n'est pas un jugement sur la politique menée. « = » : écart plus petit que le dernier chiffre affiché. Touche « i » pour les sources et le statut de vérification de chaque indicateur.</p></div>`;
}

/* ------------------------------------------------------------------ priorités du joueur */

export function prioritiesHtml(store: Store, sc: Scorecard): string {
  const w = store.content.scorecard?.weights;
  if (!w) return "";
  const weights = store.state.weights;
  const counted = sc.indicators.filter((i) => i.counted);
  const changed = Object.keys(weights).length > 0;
  const share = (x: number, t: number) => (t > 0 ? `${int(Math.round((x / t) * 100))} %` : "—");
  const t = weightedShares(sc.indicators, weights, w.default, "vs_trend");
  const p = weightedShares(sc.indicators, weights, w.default, "vs_plf");
  const sliders = counted
    .map((i) => {
      const v = weights[i.id] ?? w.default;
      return `<div class="lever wgt"><div><div class="n"><label for="w-${e(i.id)}">${e(i.label)}</label></div><div class="s">${e(i.unit)}</div></div>
        <div class="val" id="vw-${e(i.id)}">${weightLabel(v, w.max)}</div>
        <input id="w-${e(i.id)}" type="range" min="${w.min}" max="${w.max}" step="${w.step}" value="${v}" data-weight="${e(i.id)}" aria-valuetext="${weightLabel(v, w.max)}"></div>`;
    })
    .join("");
  return `<details class="more prio"${changed ? " open" : ""}><summary>Mes priorités (facultatif)</summary>
    <p class="sm mut">Le jeu ne classe pas les indicateurs. Si certains comptent plus pour toi, donne-leur plus de poids : le bilan ci-dessus ne change pas, seul ton résumé personnel en tient compte. Les priorités voyagent avec le lien de partage.</p>
    <p class="sm" id="prio-out">${changed ? "Selon tes priorités" : "À poids égaux"} : <b>${share(t.better, t.total)}</b> du poids du côté « mieux que si rien ne change » (${share(t.worse, t.total)} moins bien) ; <b>${share(p.better, p.total)}</b> face au budget du gouvernement (${share(p.worse, p.total)} moins bien).</p>
    ${sliders}
    ${changed ? `<div class="row"><button type="button" class="sec" data-action="reset-weights">Remettre des poids égaux</button></div>` : ""}</details>`;
}

function weightLabel(v: number, max: number): string {
  return v === 0 ? "ne compte pas" : `${int(v)} / ${int(max)}`;
}

/* ------------------------------------------------------------------ « Pourquoi ? » */

const KIND: Record<GapItem["kind"], string> = { measure: "mesure", event: "décision de crise", financing: "financement", reference: "budget du gouvernement" };

export function whyHtml(store: Store, sc: Scorecard, why: ScorecardExplanation | null): string {
  if (!why) return `<div class="card" id="why"><h2>Pourquoi ?</h2><p class="hint">Explication indisponible pour cette partie.</p></div>`;
  const st = store.state;
  const ind = sc.indicators.find((i) => i.id === st.scIndicator) ?? sc.indicators[0]!;
  const x = why.indicators.find((y) => y.id === ind.id);
  const view = st.scView;
  const g = x ? (view === "plf" ? x.vs_plf : x.vs_trend) : null;
  const opts = sc.indicators.map((i) => `<option value="${e(i.id)}"${i.id === ind.id ? " selected" : ""}>${e(i.label)} (${e(i.unit)})</option>`).join("");
  const seg = `<div class="seg" role="group" aria-label="Comparer à">
    <button type="button" class="segb" data-scview="trend" aria-pressed="${view === "trend"}">face à « si rien ne change »</button>
    <button type="button" class="segb" data-scview="plf" aria-pressed="${view === "plf"}">face au budget du gouvernement</button></div>`;
  let body = "";
  if (g) {
    const eps = Math.pow(10, -ind.decimals) / 2;
    const items = g.items.filter((it) => Math.abs(it.delta) >= eps);
    const max = Math.max(eps, ...items.map((it) => Math.abs(it.delta)), Math.abs(g.residual ?? 0));
    const sense = (d: number): ScorecardComparison => (Math.abs(d) < eps ? "equal" : (ind.better_is === "lower" ? -d : d) > 0 ? "better" : "worse");
    const li = (kind: string, label: string, d: number) => `<li class="attr"><div class="ah"><span class="badge mut">${e(kind)}</span> <b>${e(label)}</b></div>
      <div class="ab"><span class="abar"><i class="n" style="width:${((Math.abs(d) / max) * 100).toFixed(1)}%"></i></span><span class="xs num">${fmtDelta(ind, d)} ${e(ind.unit)} ${markHtml(sense(d), "la colonne")}</span></div></li>`;
    const list = items.slice(0, 8).map((it) => li(KIND[it.kind], it.kind === "reference" ? `Absent de ton programme : ${it.label}` : it.label, it.delta)).join("");
    const rest = items.slice(8);
    const residual = g.residual !== null && Math.abs(g.residual) >= eps ? li("interactions", "Effets combinés des éléments entre eux", g.residual) : "";
    body = `<p class="sm">${e(g.rationale)}</p>
      ${items.length || residual ? `<ul class="attrs">${list}${residual}</ul>${rest.length ? `<details class="more"><summary>Voir les ${int(rest.length)} autres</summary><ul class="attrs">${rest.map((it) => li(KIND[it.kind], it.label, it.delta)).join("")}</ul></details>` : ""}` : ""}
      <p class="hint">Chaque ligne : l'écart que produit ce seul élément (retiré de ta partie, ou réponse de crise remplacée par la réponse minimale)${view === "plf" ? ", moins ce que le budget du gouvernement produit par le même élément" : ""}. Les crises elles-mêmes sont communes aux colonnes et n'entrent pas dans l'écart. Les lignes ne s'additionnent pas exactement : le reste est l'effet combiné.</p>`;
  }
  return `<div class="card" id="why"><h2>Pourquoi ?</h2>
    <label class="sm" for="sc-ind">Indicateur</label> <select id="sc-ind" data-scind>${opts}</select>
    ${seg}${body}</div>`;
}

/* ------------------------------------------------------------------ guichet */

/** « Ce que ça change au guichet » : phrase officielle de chaque indicateur de service, toi / si rien ne change / départ. */
export function servicesHtml(store: Store, sc: Scorecard): string {
  const rows = sc.indicators
    .filter((i) => i.group === "services")
    .map((i) => {
      const kpi = defOf(store, i.id)?.kpi;
      const t = TANGIBLE_DEFS.find((d) => d.key === kpi);
      const say = (v: number | null) => (v === null ? "—" : t ? t.sentence(v) : `${fmtValue(i, v)} ${i.unit}`);
      return `<li class="tg"><b>${e(i.label)}</b>${i.source_status === "mecanique_non_sourcee" ? ` <span class="stat none">relation crédits → indicateur non sourcée</span>` : ""}<br>
        En ${sc.year} : ${e(say(i.player))}
        <br><span class="sm mut">si rien ne change : ${e(say(i.trend))}${i.start !== null ? ` · ${sc.start_year} : ${e(say(i.start))}` : ""}</span></li>`;
    });
  return rows.length
    ? `<div class="card"><h2>Ce que ça change au guichet</h2><ul class="tglist">${rows.join("")}</ul><p class="hint">Définitions officielles (DREES, ART, SDSE, DEPP, DAP) ; valeurs de départ publiées. Le lien entre les crédits d'une mission et l'indicateur est une élasticité encore à estimer : ces écarts illustrent un sens, pas une prévision.</p></div>`
    : "";
}

/** Interrupteur « mode réaliste » (ADR 0015) : défaut activé ; désactivé, les conventions de jeu peuvent interrompre le mandat. */
export function realisticToggle(store: Store, id = "rm-res"): string {
  const on = store.state.realistic;
  return `<div class="switch"><input type="checkbox" id="${id}" data-realistic ${on ? "checked" : ""}><label for="${id}"><b>Mode réaliste</b> : sans mécaniques politiques non sourcées</label>
    <p class="hint">${on ? "Activé : ni censure ni crise de dette déclenchée par l'indice de confiance des marchés ; le mandat va à son terme." : "Désactivé : la censure (capital politique, majorité) et la crise de dette (indice de confiance) peuvent interrompre le mandat. Ce sont des conventions de jeu, pas des mécaniques sourcées."}</p></div>`;
}
